import { useCallback, useEffect, useRef, useState } from "react";

const isEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  );
};

export function useUndoPoints({ setPoints }) {
  const historyRef = useRef([]);
  const isUndoingRef = useRef(false);

  const [canUndo, setCanUndo] = useState(false);

  const clone = (pts) => pts?.map((p) => ({ ...p }));

  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const init = useCallback((initialPoints) => {
    historyRef.current = [clone(initialPoints || [])];
    setCanUndo(false);
  }, []);

  const commit = (points) => {
    const history = historyRef.current;
    const last = history.at(-1);

    if (last && isEqual(last, points)) return; // из-за отслеживания input бывают дубли

    history.push(clone(points));
    setCanUndo(history.length > 1);
  };

  const undo = useCallback(() => {
    if (historyRef.current.length < 2) return;

    isUndoingRef.current = true;

    historyRef.current.pop();

    const prev = historyRef.current.at(-1);
    setPoints(clone(prev));

    setCanUndo(historyRef.current.length > 1);
  }, [setPoints]);

  // Ctrl / Cmd + Z
  useEffect(() => {
    const onKeyDown = (e) => {
      const isUndoKey = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z";

      if (!isUndoKey) return;
      if (isEditableTarget(e.target)) return;

      e.preventDefault();
      undo();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo]);

  return { undo, canUndo, init, commit };
}
